import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Chip,
  Box,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPlus, FaEdit, FaTrash, FaCloudUploadAlt, FaCloudDownloadAlt, FaEye, FaEyeSlash, FaSearch } from 'react-icons/fa';
import { Container, Header, HeaderLeft, BackButton, Title, Content, HeaderButton, ControlsContainer } from '../../common/styles';
import { MainContainer, ActionsGrid, ActionCard, ActionCardContent, ActionDetailsGrid, ItalicText, ActionButtons, ColorGrid, ColorOption } from './styles';
import EmptyState from '../../common/EmptyState';
import Modal from '../../common/Modal';
import ConfirmationModal from '../../common/ConfirmationModal';
import LoadingSpinner from '../../common/LoadingSpinner';
import { useAlert } from '../../../contexts/AlertContext';
import { permissionsApi } from '../../../services/api/permissionsApi';
import { toLocalDate } from '../../../utils/dateOnly';

// Define predefined colors for the color picker
const PREDEFINED_COLORS = [
  '#2196F3', '#4CAF50', '#FF9800', '#F44336', 
  '#9C27B0', '#00BCD4', '#607D8B', '#795548',
  '#E91E63', '#8BC34A', '#009688', '#3F51B5'
];

const Actions = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [searchTerm, setSearchTerm] = useState('');
  const [actionsData, setActionsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: 'create', // 'create' or 'edit'
    action: null, // For edit mode
    formData: {
      name: '',
      description: '',
      color: '#2196F3'
    },
    submitting: false
  });
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    action: null,
    deleting: false
  });

  // Load actions data on component mount
  useEffect(() => {
    loadActions();
  }, []);

  const loadActions = async () => {
    try {
      setLoading(true);
      const actions = await permissionsApi.getAllActions();
      setActionsData(actions);
    } catch (error) {
      console.error('Error loading actions:', error);
      showAlert(error.message || 'Failed to load actions', 'error');
      setActionsData([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredActions = actionsData.filter(action => 
    action.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (action.description && action.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handlePublishToggle = async (action) => {
    try {
      const updatedAction = action.isPublished 
        ? await permissionsApi.unpublishAction(action.id)
        : await permissionsApi.publishAction(action.id);
      
      setActionsData(prevActions => 
        prevActions.map(a => 
          a.id === action.id ? updatedAction : a
        )
      );
      
      showAlert(
        `Action ${action.isPublished ? 'unpublished' : 'published'} successfully`, 
        'success'
      );
    } catch (error) {
      console.error('Error toggling action publish status:', error);
      showAlert(error.message || `Failed to ${action.isPublished ? 'unpublish' : 'publish'} action`, 'error');
    }
  };

  const getActionColor = (actionName, actionData = null) => {
    // If we have action data with a color field, use that
    if (actionData && actionData.color) {
      return actionData.color;
    }
    
    // Otherwise fall back to predefined colors
    const colors = {
      'read': '#2196F3',
      'create': '#4CAF50',
      'update': '#FF9800',
      'delete': '#F44336',
      'manage': '#9C27B0',
      'publish': '#00BCD4',
      'export': '#607D8B',
      'import': '#795548',
      'approve': '#8BC34A',
      'reject': '#E91E63',
      'assign': '#3F51B5',
      'archive': '#9E9E9E',
      'restore': '#009688'
    };
    return colors[actionName] || '#757575';
  };

  const getStatusColor = (isPublished) => {
    return isPublished ? '#E6F4EA' : '#F3F4F6';
  };

  const getStatusTextColor = (isPublished) => {
    return isPublished ? '#1E8E3E' : '#6B7280';
  };

  const getStatusText = (isPublished) => {
    return isPublished ? 'Published' : 'Draft';
  };

  // Modal handlers
  const openCreateModal = () => {
    setModalState({
      isOpen: true,
      mode: 'create',
      action: null,
      formData: {
        name: '',
        description: '',
        color: '#2196F3'
      }
    });
  };

  const openEditModal = (action) => {
    setModalState({
      isOpen: true,
      mode: 'edit',
      action: action,
      formData: {
        name: action.name,
        description: action.description || '',
        color: action.color || getActionColor(action.name)
      },
      submitting: false
    });
  };

  const handleModalClose = () => {
    setModalState({
      isOpen: false,
      mode: 'create',
      action: null,
      formData: {
        name: '',
        description: '',
        color: '#2196F3'
      },
      submitting: false
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setModalState(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        [name]: value
      }
    }));
  };

  const handleColorSelect = (color) => {
    setModalState(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        color: color
      }
    }));
  };

  const isFormValid = () => {
    const { name, color } = modalState.formData;
    return name.trim() && color.trim();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) return;

    const { name, description, color } = modalState.formData;
    
    try {
      setModalState(prev => ({ ...prev, submitting: true }));
      
      if (modalState.mode === 'create') {
        // Create new action
        const newAction = await permissionsApi.createAction({
          name: name.toLowerCase(),
          description: description || undefined,
          color: color || undefined
        });
        
        setActionsData(prev => [...prev, newAction]);
        showAlert('Action created successfully', 'success');
      } else {
        // Edit existing action
        const updatedAction = await permissionsApi.updateAction(modalState.action.id, {
          description: description || undefined,
          color: color || undefined
        });
        
        setActionsData(prev => prev.map(action => 
          action.id === modalState.action.id ? updatedAction : action
        ));
        showAlert('Action updated successfully', 'success');
      }

      handleModalClose();
    } catch (error) {
      console.error('Error saving action:', error);
      showAlert(error.message || `Failed to ${modalState.mode === 'create' ? 'create' : 'update'} action`, 'error');
    } finally {
      setModalState(prev => ({ ...prev, submitting: false }));
    }
  };

  // Delete handlers
  const handleDeleteClick = (action) => {
    setDeleteModal({
      open: true,
      action
    });
  };

  const handleDelete = async () => {
    if (!deleteModal.action) return;
    
    try {
      setDeleteModal(prev => ({ ...prev, deleting: true }));
      
      await permissionsApi.deleteAction(deleteModal.action.id);
      
      // Remove action from the list
      setActionsData(prev => prev.filter(action => action.id !== deleteModal.action.id));
      
      showAlert('Action deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting action:', error);
      showAlert(error.message || 'Failed to delete action', 'error');
    } finally {
      // Close the modal
      setDeleteModal({ open: false, action: null, deleting: false });
    }
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/permissions')}>
            <FaArrowLeft /> Back to Permissions
          </BackButton>
          <Title>Actions</Title>
        </HeaderLeft>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <HeaderButton onClick={() => setShowControls(!showControls)}>
            {showControls ? <FaEyeSlash /> : <FaEye />}
            {showControls ? 'Hide Panels' : 'Show Panels'}
          </HeaderButton>
        </Box>
      </Header>

      <Content>
        <ControlsContainer $show={showControls} style={{ marginBottom: '20px' }}>
          <Box sx={{ 
            display: 'flex', 
            gap: '16px', 
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <TextField
              placeholder="Search Actions"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }
              }}
              sx={{
                flex: 1,
                maxWidth: '500px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  height: '40px',
                  fontSize: '14px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2563eb',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2563eb',
                  }
                },
                '& .MuiInputBase-input': {
                  padding: '8px 14px',
                }
              }}
            />

            <HeaderButton 
              onClick={openCreateModal}
              sx={{
                minWidth: 'auto',
                height: '32px',
                padding: '0 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                alignSelf: 'flex-start',
                '&:hover': {
                  backgroundColor: '#1d4ed8',
                }
              }}
            >
              <FaPlus style={{ marginRight: '8px' }} /> Create Action
            </HeaderButton>
          </Box>
        </ControlsContainer>

        <MainContainer show={showControls}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2
          }}>
            <Typography 
              sx={{ 
                color: '#64748B',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <Box component="span" sx={{ fontWeight: 600 }}>
                {loading ? '...' : filteredActions.length}
              </Box>
              {filteredActions.length === 1 ? 'action' : 'actions'} found
            </Typography>
          </Box>

          {loading ? (
            <LoadingSpinner message="Loading actions..." />
          ) : filteredActions.length === 0 ? (
            <EmptyState
              title="No actions found"
              description={searchTerm ? 
                "No actions match your search criteria. Try adjusting your search term." : 
                "No actions have been created yet. Click the 'Create Action' button to create your first action."}
            />
          ) : (
            <ActionsGrid>
              {filteredActions.map((action) => (
                <ActionCard key={action.id || action.name}>
                  <ActionCardContent>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      mb: 2,
                      gap: 1
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={action.name}
                          variant="outlined"
                          sx={{ 
                            borderColor: getActionColor(action.name, action),
                            color: getActionColor(action.name, action),
                            backgroundColor: 'transparent',
                            fontWeight: 500,
                            fontSize: '13px',
                            height: '24px',
                            minWidth: 'fit-content',
                            '& .MuiChip-label': {
                              padding: '0 8px',
                              whiteSpace: 'nowrap'
                            }
                          }}
                        />
                      </Box>
                      <Chip
                        label={getStatusText(action.isPublished)}
                        size="small"
                        sx={{ 
                          backgroundColor: getStatusColor(action.isPublished),
                          color: getStatusTextColor(action.isPublished),
                          border: '1px solid',
                          borderColor: `${getStatusTextColor(action.isPublished)}20`,
                          height: '24px',
                          minWidth: 'fit-content',
                          '& .MuiChip-label': {
                            fontSize: '13px',
                            padding: '0 8px',
                            fontWeight: 500,
                            whiteSpace: 'nowrap'
                          }
                        }}
                      />
                    </Box>
                    
                    <ActionDetailsGrid>
                      {action.description ? (
                        <Typography color="#333">
                          {action.description}
                        </Typography>
                      ) : (
                        <ItalicText>No description provided</ItalicText>
                      )}
                      {action.publishedAt && action.isPublished && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Published: {toLocalDate(action.publishedAt)?.toLocaleDateString()}
                          </Typography>
                        </Box>
                      )}
                    </ActionDetailsGrid>

                    <ActionButtons>
                      {action.isPublished ? (
                        <IconButton
                          onClick={() => handlePublishToggle(action)}
                          color="primary"
                          size="small"
                          title="Unpublish action"
                        >
                          <FaCloudDownloadAlt />
                        </IconButton>
                      ) : (
                        <IconButton
                          onClick={() => handlePublishToggle(action)}
                          color="primary"
                          size="small"
                          title="Publish action"
                        >
                          <FaCloudUploadAlt />
                        </IconButton>
                      )}
                      <IconButton
                        onClick={() => openEditModal(action)}
                        color="primary"
                        size="small"
                        title="Edit action"
                      >
                        <FaEdit />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteClick(action)}
                        color="error"
                        size="small"
                        title="Delete action"
                      >
                        <FaTrash />
                      </IconButton>
                    </ActionButtons>
                  </ActionCardContent>
                </ActionCard>
              ))}
            </ActionsGrid>
          )}
        </MainContainer>
      </Content>

      {/* Create/Edit Action Modal */}
      <Modal
        isOpen={modalState.isOpen}
        onClose={handleModalClose}
        title={modalState.mode === 'create' ? 'Create Action' : 'Edit Action'}
        onSubmit={handleSubmit}
        submitText={modalState.mode === 'create' ? 'Create' : 'Update'}
        submitDisabled={!isFormValid() || modalState.submitting}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Name"
            name="name"
            required
            fullWidth
            variant="outlined"
            placeholder="Enter action name (e.g. read, create, update)"
            value={modalState.formData.name}
            onChange={handleInputChange}
            autoComplete="off"
            disabled={modalState.mode === 'edit'} // Name cannot be changed in edit mode
          />
          <TextField
            label="Description"
            name="description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            placeholder="Enter action description"
            value={modalState.formData.description}
            onChange={handleInputChange}
            autoComplete="off"
          />
          
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Color</Typography>
            <TextField
              label="Color Code"
              name="color"
              required
              fullWidth
              variant="outlined"
              placeholder="Enter color hex code (e.g. #2196F3)"
              value={modalState.formData.color}
              onChange={handleInputChange}
              autoComplete="off"
                             sx={{ mb: 2 }}
               slotProps={{
                 input: {
                   endAdornment: (
                     <Box 
                       sx={{ 
                         width: 24, 
                         height: 24, 
                         backgroundColor: modalState.formData.color,
                         borderRadius: '50%',
                         border: '1px solid rgba(0,0,0,0.1)',
                         ml: 1,
                         display: 'inline-block',
                         flexShrink: 0,
                         minWidth: '24px'
                       }} 
                     />
                   ),
                 }
               }}
            />
            <Typography variant="body2" sx={{ mb: 1 }}>Or select from predefined colors:</Typography>
            <ColorGrid>
              {PREDEFINED_COLORS.map((color) => (
                <ColorOption
                  key={color}
                  color={color}
                  selected={modalState.formData.color === color}
                  onClick={() => handleColorSelect(color)}
                  aria-label={`Select color ${color}`}
                />
              ))}
            </ColorGrid>
          </Box>
        </Box>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, action: null, deleting: false })}
        onConfirm={handleDelete}
        title="Delete Action"
        message={`Are you sure you want to delete the "${deleteModal.action?.name}" action? This action cannot be undone.`}
        loading={deleteModal.deleting}
      />
    </Container>
  );
};

export default Actions;